/**
 * Shared assistant-ui seed builders for the `/chat` rehydration path.
 *
 * Extracted from `chat-history-list.tsx` (PLT-5514) so both the history-row
 * reopen path AND the live-injection path (`use-conversation-events.ts` →
 * `UprightChatInner`) build the seeded `ExportedMessageRepository` from
 * persisted messages the SAME way. Keeping one copy avoids the
 * card-reconstruction logic (and its hard-won crash guards) drifting between
 * the two surfaces, and avoids an import cycle (the list and the live path
 * would otherwise have to import from each other).
 *
 * The full history of why each guard exists lives in the doc comments below;
 * see PLT-4947 (card replay), PLT-5019 (atomic `import`), PLT-5137 / PLT-5232 /
 * PLT-5268 (per-message + chain-level crash guards) and PLT-5429 (passive
 * presentation tools).
 */
import {
  ExportedMessageRepository,
  type ThreadAssistantMessagePart,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import { type ChatConversationDetailMessage } from './chat-history-data';
import { trailOnlyResult } from '../tool-trail-steps';

const SEEDED_TOOL_CALL_ID_PREFIX = 'seed-';

/**
 * Whether a tool call was rebuilt from a stored conversation rather than streamed as the agent
 * answered, read off the id `reconstructContentParts` gives it. For a card whose live mount does
 * something only once, such as counting the proposal it shows (PLT-7416).
 */
export function isSeededToolCallId(toolCallId: string): boolean {
  return toolCallId.startsWith(SEEDED_TOOL_CALL_ID_PREFIX);
}

/**
 * PLT-4947: the lean rehydration event shape persisted by the backend
 * (`toRehydrationContentEvents` in `chat-transcript/parser.ts`). A structural
 * type rather than an import — the backend's `RehydrationContentEvent` is not
 * reachable from `src/ui` — and every field is optional because `contentEvents`
 * arrives as `unknown[]`: we validate each entry defensively below rather than
 * trust the wire shape.
 *
 * Exported so fixtures can be authored against the shape this file actually
 * branches on (PLT-6123, `upright-chat.fixtures.ts`) instead of re-declaring it
 * and drifting. Not part of the wire contract — nothing at runtime should treat
 * a value typed as this as validated.
 */
export interface PersistedRehydrationEvent {
  kind?: string;
  text?: string;
  name?: string;
  args?: Record<string, unknown>;
  ui?: { appName?: string; uiOnly?: boolean };
  result?: unknown;
  /** PLT-7912: on a `tool-step`, the call failed. */
  failed?: boolean;
}

/**
 * Reconstruct assistant-ui content parts from a persisted message.
 *
 * Returns the rich parts (text, MCP App card tool-calls, the passive
 * presentation tool calls — Verdict callout / KPI tiles — and the tool trail's
 * steps) when the row carries a usable `contentEvents` projection; otherwise
 * returns `null` so the caller falls back to the single plain-text part. The `toolCallId` is derived
 * deterministically from the conversation id + message index + part index so
 * it's stable across re-seeds of one conversation and unique within the thread
 * — assistant-ui keys content parts by `toolCallId` at thread scope (see the
 * duplicate-key breakage noted in `upright-chat-adapter.ts`/PLT-4452). The
 * conversation id keeps it unique across conversations too: `McpToolUI` keys its
 * card on it, and two same-shaped chats would otherwise produce the same id and
 * reuse one card's iframe when the user switches between them (PLT-6891).
 *
 * Typed as `ThreadAssistantMessagePart[]` because these parts only ever appear
 * on assistant turns; the card branch hands `McpToolUI` the same `{ data, _ui }`
 * `result` wrapper the live adapter builds (`upright-chat-adapter.ts`).
 */
function reconstructContentParts(
  message: ChatConversationDetailMessage,
  conversationId: string,
): ThreadAssistantMessagePart[] | null {
  // Only assistant turns carry cards; user messages are always plain text.
  if (message.role !== 'assistant' || !Array.isArray(message.contentEvents) || message.contentEvents.length === 0) {
    return null;
  }

  // Built as a loosely-typed array and cast to `ThreadAssistantMessagePart[]`
  // at the boundary — same pattern the live adapter uses for its streamed
  // content (`buildContent` in `upright-chat-adapter.ts`). The persisted args
  // are JSON by construction, so the cast is sound.
  const parts: Array<Record<string, unknown>> = [];
  let stepParts = 0;
  message.contentEvents.forEach((raw, partIndex) => {
    const event = raw as PersistedRehydrationEvent;
    if (event.kind === 'text') {
      if (typeof event.text === 'string' && event.text.length > 0) {
        parts.push({ type: 'text', text: event.text });
      }
      return;
    }
    if (event.kind === 'tool-call' && typeof event.ui?.appName === 'string' && event.ui.appName.length > 0) {
      const args = event.args ?? {};
      parts.push({
        type: 'tool-call',
        // Deterministic + unique: the conversation id separates chats, the
        // message index is unique per turn and the part index within the turn.
        toolCallId: `${SEEDED_TOOL_CALL_ID_PREFIX}${conversationId}-${message.messageIndex}-${partIndex}`,
        toolName: typeof event.name === 'string' ? event.name : 'unknown',
        args,
        argsText: JSON.stringify(args),
        // Mirror the live adapter's `{ data, _ui }` wrapper so McpToolUI's
        // `result._ui.appName` branch fires and mounts the iframe.
        result: { data: event.result, _ui: event.ui },
      });
      return;
    }
    // PLT-5429: passive presentation tools (Verdict callout, KPI stat tiles).
    // The backend persists these as `uiOnly` tool calls whose payload is the
    // stored `result` — what the component actually drew live, after the server
    // trimmed anything overshooting a layout cap and after validation either
    // passed or refused the call. The toolName-matched UIs (`EmitCalloutToolUI`,
    // `PresentHeadlineKpisToolUI`) render from `result?.data ?? args`, so we
    // wrap it as the result `data` to mirror exactly what they received live.
    //
    // PLT-6108: `args` is the fallback for turns persisted before the result was
    // captured. It is the model's raw request rather than the rendered payload,
    // so on those older turns a refused callout still replays in full — the
    // stored data to do better simply isn't there. `toolName` must round-trip
    // verbatim for assistant-ui to dispatch to the matching tool UI.
    if (event.kind === 'tool-call' && event.ui?.uiOnly === true && typeof event.name === 'string') {
      const args = event.args ?? {};
      parts.push({
        type: 'tool-call',
        toolCallId: `${SEEDED_TOOL_CALL_ID_PREFIX}${conversationId}-${message.messageIndex}-${partIndex}`,
        toolName: event.name,
        args,
        argsText: JSON.stringify(args),
        result: { data: event.result ?? args, _ui: event.ui },
      });
      return;
    }
    // PLT-7912: a tool the turn used that has no card to replay, persisted as its call alone so
    // the reopened turn's tool trail lists the same steps as the live one. The trail-only result
    // marks it done (or failed) without pretending to be what the tool returned.
    if (event.kind === 'tool-step' && typeof event.name === 'string') {
      const args = event.args ?? {};
      stepParts += 1;
      parts.push({
        type: 'tool-call',
        toolCallId: `${SEEDED_TOOL_CALL_ID_PREFIX}${conversationId}-${message.messageIndex}-${partIndex}`,
        toolName: event.name,
        args,
        argsText: JSON.stringify(args),
        result: trailOnlyResult(event.failed === true),
      });
    }
  });

  // If nothing usable came back (e.g. all text segments were empty), let the
  // caller fall back to the plain-text body rather than seed an empty turn.
  if (parts.length === 0) return null;
  // A turn the projection kept only steps of still shows the answer it had before those steps
  // were persisted: its plain-text body, under the trail.
  if (stepParts === parts.length && typeof message.content === 'string' && message.content.length > 0) {
    parts.push({ type: 'text', text: message.content });
  }
  return parts as unknown as ThreadAssistantMessagePart[];
}

/**
 * Convert one past message into the `ThreadMessageLike` shape that
 * `ExportedMessageRepository.fromArray` consumes (PLT-5019). Assistant turns
 * with persisted card events are reconstructed into text + MCP App card parts
 * (PLT-4947); everything else forwards the plain `content` text.
 *
 * No `startRun` flag is needed: `import` seeds the message tree directly
 * without going through the run path, so a seeded `user` message can never
 * trigger the chat adapter or send a duplicate turn to the backend.
 */
function buildSeededMessage(message: ChatConversationDetailMessage, conversationId: string): ThreadMessageLike {
  const reconstructed = reconstructContentParts(message, conversationId);
  // String-guard the plain-text fallback. assistant-ui's `fromThreadMessageLike`
  // runs `part.text.trim()` on assistant text/reasoning parts *without* checking
  // the value is a string, so a `null`/`undefined` `content` column — e.g. an
  // assistant turn that rendered only cards and never persisted prose, or one
  // written by the fire-and-forget `persistChatTurn` gap — would throw a
  // `TypeError` and abort the import of the *entire* conversation. Coercing a
  // non-string to `''` makes such a turn seed as empty content (assistant-ui
  // trims the empty part to length 0 and filters it out, rendering nothing)
  // instead of crashing every other turn in the conversation. An empty-string
  // `content` was already safe — only genuinely non-string content threw.
  const text = typeof message.content === 'string' ? message.content : '';
  // PLT-5741: stamp assistant turns with the coordinates their feedback row is
  // keyed by — `(conversationId, messageIndex)` — so the reopened thread's
  // feedback control targets the same `chat_message_feedback` row the live
  // adapter would (which stamps the identical `metadata.custom` shape). User
  // turns carry no vote control, so they need no marker.
  const metadata =
    message.role === 'assistant'
      ? {
          custom: {
            threadId: conversationId,
            messageIndex: message.messageIndex,
            // Seed the persisted vote so the control renders filled on reopen.
            // Absent on unrated turns; the control treats missing as "no vote".
            ...(message.feedback ? { feedback: message.feedback } : {}),
          },
        }
      : undefined;
  return {
    role: message.role,
    content: reconstructed ?? [{ type: 'text', text }],
    ...(metadata ? { metadata } : {}),
  };
}

/**
 * Per-message guard around `buildSeededMessage` — the belt-and-suspenders layer
 * over its targeted string-guard.
 *
 * `buildSeededMessage` kills the one *confirmed* crash (a non-string `content`
 * reaching `part.text.trim()`). This wrapper additionally protects against any
 * *other* stored shape assistant-ui might reject that we haven't seen yet: it
 * runs the single converted message through `ExportedMessageRepository.fromArray`
 * in isolation. `fromArray` is pure and maps assistant-ui's `fromThreadMessageLike`
 * over each element, so a one-element array exercises the exact per-message
 * conversion that throws, with no shared state or side effects. If it throws,
 * only this one turn degrades to a plain-text placeholder (and the rejected
 * shape is logged); the rest of the conversation still opens, rather than one
 * bad turn blocking the whole import.
 */
export function toImportableSeededMessage(
  message: ChatConversationDetailMessage,
  conversationId: string,
): ThreadMessageLike {
  const seeded = buildSeededMessage(message, conversationId);
  try {
    ExportedMessageRepository.fromArray([seeded]);
    return seeded;
  } catch (err) {
    console.error('[chat-history] failed to seed message; using placeholder', {
      conversationId,
      messageIndex: message.messageIndex,
      role: message.role,
      error: err,
    });
    return {
      role: message.role,
      content: [{ type: 'text', text: '[This message could not be displayed.]' }],
    };
  }
}

/**
 * Build the full `ExportedMessageRepository` from ALL persisted messages of a
 * conversation. Shared by the reopen path (`chat-history-list.tsx`) and the
 * live-injection path (`use-conversation-events.ts`).
 *
 * `ExportedMessageRepository.fromArray` wires each message's `parentId` to the
 * previous one and `import` sets the head to the resulting leaf, producing a
 * guaranteed linear chain (`user0 -> asst0 -> user1 -> ...`) and avoiding the
 * microtask parent-resolution race the old incremental-append loop hit
 * (PLT-5019). `toImportableSeededMessage` validates each turn in isolation, so
 * one unconvertible message degrades to a placeholder rather than aborting the
 * whole import (PLT-5268); the caller should still wrap this in a try/catch as
 * a final backstop for chain-level guards that only fire on the full array
 * (PLT-5232).
 */
export function buildSeededRepository(
  messages: ChatConversationDetailMessage[],
  conversationId: string,
): ReturnType<typeof ExportedMessageRepository.fromArray> {
  const seededMessages = messages.map(message => toImportableSeededMessage(message, conversationId));
  return ExportedMessageRepository.fromArray(seededMessages);
}
